import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useStore } from '../stores/store';
import useOfflineStatus from './use-offline-status.hook';
import useNavigateNewLocale from './use-navigate-to-new-locale.hook';
import { client, envConfig } from '../config';
import { Modals } from '../constants/modals';
import { isMeest } from '../utils/client/client.utils';
import { useAppVersionCheck } from './use-app-version-check.hook';
import { AppRoute } from '../constants/route';

let didInit = false;
// eslint-disable-next-line import/no-mutable-exports
export const scrollbarWidth = 0;

export const useInitApp = () => {
  const {
    commonStore: {
      toastErrorMissingPhoneNumber,
      appLoaded,
      isLaunchNotificationShown,
      setLaunchNotificationShown,
      analyticsManager,
      setAnalyticsInitialized,
    },
    userStore: { user },
    localizationsStore: {
      fetchCountries,
      selectedLocalization,
      selectedCountry,
      availableLocalizations,
    },
    modalStore: { open },
    marketingStore: { countriesWithLaunchNotificationModal },
  } = useStore();

  const auth = useAuth();

  const { t } = useTranslation();

  const location = useLocation();
  const isAuthPage = location.pathname.includes('auth');
  const isOauthPage =
    location.pathname.includes(AppRoute.OAUTH2_CODE) ||
    location.pathname.includes(AppRoute.OAUTH2_LOGOUT) ||
    location.pathname.includes(AppRoute.OAUTH2_RENEW);

  useAppVersionCheck();
  useOfflineStatus();
  const navigateToNewLocale = useNavigateNewLocale();

  useEffect(() => {
    if (!didInit) {
      didInit = true;
      fetchCountries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize analytics services only after we know our locale
  useEffect(() => {
    if (!selectedCountry?.country_code) return;

    analyticsManager.initialize();
    setAnalyticsInitialized(true);
  }, [
    selectedCountry?.country_code,
    analyticsManager,
    setAnalyticsInitialized,
  ]);

  useEffect(() => {
    const showLaunchNotification =
      isMeest(client) &&
      selectedCountry?.country_code &&
      countriesWithLaunchNotificationModal.includes(
        selectedCountry.country_code
      ) &&
      envConfig.REACT_APP_SHOW_NEW_LAUNCH_NOTIFICATION &&
      !isLaunchNotificationShown;

    if (showLaunchNotification) {
      open({
        id: Modals.LAUNCH_NOTIFICATION,
        name: Modals.LAUNCH_NOTIFICATION,
      });
      setLaunchNotificationShown(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry?.country_code, countriesWithLaunchNotificationModal]);

  // Sync user's language with app's language
  useEffect(() => {
    if (
      user?.lang &&
      selectedLocalization?.code &&
      availableLocalizations.find((loc) => loc.code === user?.lang) &&
      user?.lang !== selectedLocalization?.code
    ) {
      navigateToNewLocale({ langCode: user?.lang });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.lang]);

  useEffect(() => {
    !user?.phone &&
      user &&
      !isAuthPage &&
      toastErrorMissingPhoneNumber(
        <>
          {t('please_add_your_phone number_in_the')}{' '}
          <b>{t('account_settings')}</b>{' '}
        </>
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showLoadingSpinner = isOauthPage
    ? auth.isLoading
    : !appLoaded || auth.isLoading || !selectedLocalization || !selectedCountry;

  return {
    showLoadingSpinner,
  };
};
